import { useElementSize } from '@mantine/hooks';
import {
  BaseSelectProps,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { CoreFieldEnumOpt } from 'providers/contexts/extensions/DynamicFieldData';
import { memo, useCallback, useMemo } from 'react';
import { bloxValues } from 'themes/constants';
import { resolveSxProps } from 'utility/styles';
import { LabelWithTooltip } from '../components/LabelWithTooltip';
import {
  DynamicFieldType,
  FieldOptions,
  TDynamicFieldOnChange,
} from '../DynamicFormField';
import { DROPDOWN_MAX_HEIGHT, getFieldLabel } from '../utility/functions';
import { getCheckRadioSx } from '../utility/styles';

/**
 * Normalize selectedValue to satisfy MUI Select's value requirements.
 *
 * multiple=true - must be an array. If not already an array, return [].
 * multiple=false - should be a string. If somehow array, takes first string.
 */
export function normalizeEnumValue(selectedValue: any, isMultiple: boolean): any {
  // Multi select should be array
  if (isMultiple) {
    if (Array.isArray(selectedValue)) {
      return selectedValue;
    }
    return [];
  }
  // Guard against incorrect type
  if (Array.isArray(selectedValue)) {
    const firstValue = selectedValue?.[0] ?? '';
    if (typeof firstValue === 'string') {
      return firstValue;
    }
    return '';
  }
  // Single select should be string
  return selectedValue ?? '';
}

/**
 * Resolve display text for selected enum value(s).
 * Used for title attribute on the Select text when closed.
 * Multi-select: comma-separated labels. Single: single label.
 */
export function getTitleProperty(
  nValue: string | string[],
  isMultiple: boolean,
  enumOpts: CoreFieldEnumOpt[],
): string {
  if (isMultiple) {
    if (!Array.isArray(nValue) || nValue.length === 0) {
      return '';
    }
    return nValue
      .map((val) => {
        const opt = enumOpts.find((o) => o?.value === val);
        const label = opt?.label ?? String(val) ?? '';
        return label;
      })
      .join(', ');
  }
  const match = enumOpts.find((o) => {
    return o?.value === nValue;
  });
  const label = match?.label ?? String(nValue) ?? '';
  return label;
}

/**
 * ---------------------------------------
 * Enumeration Dynamic Field
 * ---------------------------------------
 *
 * Renders enumeration field as a MUI Select.
 * Supports single and multi-select based on field config.
 *
 */
type EnumDynamicFieldProps = {
  fieldConfig: DynamicFieldType;
  selectedValue: any;
  disabled: boolean;
  handleValueChange: TDynamicFieldOnChange;
  tooltipText: string | null;
  fieldOptions?: FieldOptions;
  fieldSx?: SxProps<Theme>;
};

export const EnumDynamicField = memo(
  ({
    fieldConfig,
    selectedValue,
    disabled,
    handleValueChange,
    tooltipText,
    fieldOptions,
    fieldSx,
  }: EnumDynamicFieldProps) => {
    const theme = useTheme();
    const { ref: selectRef, width: selectWidth } = useElementSize<HTMLDivElement>();
    const isMultiConfig = Boolean(fieldConfig?.config?.multiple);
    const isHideLabel = Boolean(fieldConfig?.config?.hideLabel);

    // Clean string label for mui outlined
    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    const testId = useMemo(() => {
      return `enum-field-control--${fieldConfig?.name}`;
    }, [fieldConfig?.name]);

    const nValue = useMemo(() => {
      return normalizeEnumValue(selectedValue, isMultiConfig);
    }, [selectedValue, isMultiConfig]);

    const enumOpts = useMemo(() => {
      return fieldConfig?.config?.options ?? [];
    }, [fieldConfig.config?.options]);

    // Resolve title property text for browser tooltip
    // to show selected value(s) on hover of field
    const titleTxt = useMemo(() => {
      return getTitleProperty(nValue, isMultiConfig, enumOpts);
    }, [nValue, isMultiConfig, enumOpts]);

    // MUI Requires an array for multiselect ops
    const isMultiSelect = isMultiConfig && Array.isArray(nValue);

    const handleChange = useCallback(
      (e: SelectChangeEvent) => {
        handleValueChange(fieldConfig?.name, e.target.value, { options: enumOpts });
      },
      [fieldConfig?.name, handleValueChange, enumOpts],
    );

    // Renders selected items in the input area
    // - handles both single and multi select
    // - keeps multi select checkboxes out of display
    const handleRenderValue = useCallback(
      (selected: any) => {
        const isMultiSelected = Array.isArray(selected);

        // Multi-select: resolve ID to label, join with comma
        if (isMultiSelected) {
          const isEmpty = selected.length === 0;
          if (isEmpty) {
            return '';
          }
          const labels = selected.map((val) => {
            const match = enumOpts.find((o) => {
              return o?.value === val;
            });
            return match?.label ?? val;
          });
          return labels.join(', ');
        }

        // Single-select: resolve ID to its label
        const match = enumOpts.find((o) => {
          return o?.value === selected;
        });
        return match?.label ?? selected ?? '';
      },
      [enumOpts],
    );

    // Styles and related props
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    const formControlSx = useMemo(() => {
      return {
        mt: isHideLabel ? 0 : undefined,
        ...resolvedSx,
      };
    }, [isHideLabel, resolvedSx]);

    /**
     * Measure select width and popover width to match.
     */
    const selectMenuProps: BaseSelectProps['MenuProps'] = useMemo(() => {
      return {
        PaperProps: {
          sx: {
            width: selectWidth || undefined,
            maxHeight: DROPDOWN_MAX_HEIGHT,
            overflowY: 'auto',
          },
        },
      };
    }, [selectWidth]);

    // Checkbox/Radio styles
    const checkRadioSx: SxProps<Theme> = useMemo(() => {
      return getCheckRadioSx(theme);
    }, [theme]);

    return (
      <FormControl
        data-testid={testId}
        disabled={disabled}
        fullWidth={true}
        variant="outlined"
        sx={formControlSx}
      >
        {!isHideLabel && (
          <InputLabel shrink>
            <LabelWithTooltip
              label={fieldConfig?.label}
              tooltip={tooltipText}
              tooltipPlacement={fieldOptions?.tooltipPlacement}
            />
          </InputLabel>
        )}
        <Select
          ref={selectRef}
          label={isHideLabel ? undefined : fieldLabel}
          value={nValue}
          multiple={isMultiConfig}
          title={titleTxt}
          onChange={handleChange}
          displayEmpty={true}
          renderValue={handleRenderValue}
          MenuProps={selectMenuProps}
          inputProps={{
            'data-testid': `${testId}-select`,
          }}
          sx={{
            mt: isHideLabel ? 0 : undefined,
          }}
        >
          {enumOpts?.map((option: CoreFieldEnumOpt) => {
            const optValue = option?.value ?? '';
            const optLabel = option?.label ?? option?.value ?? '';
            const optDisabled = option?.disabled ?? false;
            const keyId = `enum-option-${option?.value}`;

            const isSelected = isMultiSelect
              ? nValue.includes(option?.value)
              : nValue === option?.value;

            // note: checkbox is for visual hint
            // that multiple selection is possible
            return (
              <MenuItem
                key={keyId}
                data-testid={keyId}
                value={optValue}
                disabled={optDisabled}
                title={optLabel}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: bloxValues.regularFontSizeRem,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  p: 1,
                }}
              >
                {isMultiConfig && (
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    disableRipple={true}
                    tabIndex={-1}
                    sx={checkRadioSx}
                  />
                )}
                {optLabel}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  },
);
EnumDynamicField.displayName = 'EnumerationDynamicField';
